import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { CTAButton } from "./CTAButton";
import { SERVICES, STATS, PILLARS } from "../data";

export const SectionLabel = ({ children }) => (
  <div className="kept-label brass-text mb-6" data-testid="section-label">{children}</div>
);

export const EditorialMarquee = () => (
  <div className="bg-kept-ink py-10 border-y border-kept-brass/20" data-testid="editorial-marquee">
    <Marquee speed={38} gradient={false} autoFill>
      {["Protect the investment", "Give owners their time back", "Compliance-forward", "Financially disciplined", "Prestigious management"].map((t, i) => (
        <span key={i} className="font-serif text-4xl md:text-6xl font-light text-kept-sand/90 mx-12 flex items-center gap-12">
          {t}
          <span className="inline-block w-2 h-2 rounded-full bg-kept-brass" />
        </span>
      ))}
    </Marquee>
  </div>
);

export const ServiceGrid = ({ heading = "Complete property management, zero compromise.", sub = "Every service we offer is built around one goal: protecting and growing your property investment while eliminating the day-to-day burden." }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36" data-testid="services-section">
    <div className="max-w-2xl mb-20">
      <SectionLabel>What we do</SectionLabel>
      <Reveal>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight text-kept-ink leading-tight">
          {heading}
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">{sub}</p>
      </Reveal>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-kept-ink/10 border border-kept-ink/10">
      {SERVICES.map((s, i) => (
        <Reveal key={s.n} delay={(i % 3) * 0.08}>
          <div
            className="group bg-kept-sand h-full p-10 transition-colors duration-500 hover:bg-kept-ink"
            data-testid={`service-card-${s.n}`}
          >
            <div className="font-serif text-2xl brass-text group-hover:text-kept-brass transition-colors duration-500">{s.n}</div>
            <h3 className="font-serif text-xl md:text-2xl mt-6 text-kept-ink group-hover:text-kept-sand transition-colors duration-500 leading-snug">
              {s.title}
            </h3>
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed group-hover:text-kept-sand/70 transition-colors duration-500">
              {s.body}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const StatsBand = () => (
  <section className="bg-kept-ink grain" data-testid="stats-band">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-3 gap-16 md:gap-8">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 0.12}>
          <div className="border-l border-kept-brass/40 pl-8">
            <div className="font-serif text-6xl md:text-7xl font-light text-kept-brass leading-none">{s.value}</div>
            <div className="text-kept-sand text-lg mt-5 font-medium">{s.label}</div>
            <div className="text-kept-sand/50 text-sm mt-1">{s.sub}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Pillars = ({
  heading = "Built for owners who demand excellence.",
  label = "Our approach",
}) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36" data-testid="pillars-section">
    <div className="max-w-2xl mb-20">
      <SectionLabel>{label}</SectionLabel>
      <Reveal>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight text-kept-ink leading-tight">
          {heading}
        </h2>
      </Reveal>
    </div>
    <div className="space-y-px">
      {PILLARS.map((p, i) => (
        <Reveal key={p.n} delay={i * 0.06}>
          <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start py-12 border-t border-kept-ink/10 group">
            <div className="md:col-span-2 font-serif text-5xl md:text-6xl font-light brass-text">{p.n}</div>
            <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl text-kept-ink leading-tight">{p.title}</h3>
            <p className="md:col-span-6 text-muted-foreground text-base md:text-lg leading-relaxed">{p.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const ClosingCTA = ({
  heading = "Ready to get started?",
  sub = "Let us handle tenant placement, compliance, and financial reporting, so you can focus on what matters.",
}) => (
  <section className="bg-kept-stone" data-testid="closing-cta">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 text-center">
      <Reveal>
        <motion.h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-kept-ink max-w-4xl mx-auto leading-tight">
          {heading}
        </motion.h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-muted-foreground mt-6 text-base md:text-lg max-w-2xl mx-auto">{sub}</p>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mt-12 flex justify-center">
          <CTAButton testId="closing-cta-book" />
        </div>
      </Reveal>
    </div>
  </section>
);
